import { type AppBase } from 'playcanvas';
import { warmup } from './WarmupRunner';

// @engine/core — ContextLossGuard (REBUILD_PLAN Step 7a). Relocated VERBATIM from
// packages/client/src/app/resilience.ts (the setupContextLoss function + the makeOverlay helper).
// ContextLossGuard imports warmup from WarmupRunner (keeping the R2 re-warm-before-resume order).

// resilience — the iOS WebGL context-loss recovery path (arch §2.8) + the shader warmup contract (arch §2.7.1).
// iOS Safari drops the GL context aggressively on backgrounding / memory pressure; an unhandled loss is a black
// frozen screen for returning players. The zero-asset design makes recovery cheap: every GPU resource is either
// engine-tracked (auto-restored) or a re-run of our own factories. We freeze the loop + show a DOM overlay on
// loss, and resume + re-warm on restore. This is the render-side twin of the NetClient's iOS reconnect.

function makeOverlay(): { show: () => void; hide: () => void } {
  if (typeof document === 'undefined') return { show: () => undefined, hide: () => undefined };
  const el = document.createElement('div');
  el.textContent = 'Reconnecting…';
  el.setAttribute('role', 'status');
  el.style.cssText =
    'position:fixed;inset:0;display:none;align-items:center;justify-content:center;' +
    'background:rgba(20,28,24,0.86);color:#dfe;font:600 20px system-ui,sans-serif;z-index:9999';
  document.body.appendChild(el);
  return {
    show: () => {
      el.style.display = 'flex';
    },
    hide: () => {
      el.style.display = 'none';
    },
  };
}

/**
 * Wire the iOS context-loss recovery (arch §2.8). The robust hook is the engine's device-level `devicelost` /
 * `devicerestored` events — the engine's abstraction over the canvas `webglcontextlost/restored` that fires on
 * BOTH a real iOS loss AND `device.loseContext()` (so it's exercisable in dev, the DoD requirement). On loss:
 * freeze the loop + show the a11y overlay (GDD §11.7). On restore: the engine has re-uploaded its tracked GPU
 * resources (textures, vertex buffers — cheap in a zero-asset build); we resume `autoRender` + hide the overlay.
 * The raw canvas `webglcontextlost` preventDefault backstop guarantees the browser attempts restoration on iOS.
 */
export function setupContextLoss(app: AppBase, canvas: HTMLCanvasElement): void {
  const overlay = makeOverlay();
  const device = app.graphicsDevice;

  device.on('devicelost', () => {
    app.autoRender = false;
    overlay.show();
    console.warn('[resilience] device lost — freezing + overlay (arch §2.8)');
  });
  device.on('devicerestored', () => {
    // R2 (EXECUTION_SPEC) — re-warm BEFORE re-enabling autoRender / hiding the overlay. On iOS Safari a context
    // restore forces synchronous shader RE-COMPILATION on the first rendered frame; doing it here makes that
    // multi-second stall happen while the "Reconnecting…" overlay is still up, not on the first frame the player sees.
    warmup(app);
    app.autoRender = true;
    overlay.hide();
    console.info('[resilience] device restored — re-warmed + resumed');
  });

  // Backstop for real iOS Safari: allow the browser to restore the context.
  canvas.addEventListener('webglcontextlost', (e: Event) => e.preventDefault(), false);
}
